import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./UserMenu.css";
import { logout } from "../utils/auth";
import { CurrentUser, displayNameFor, fetchCurrentUser, initialsFor } from "../utils/user";

const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mounted = true;
    void fetchCurrentUser().then((result) => {
      if (mounted) setUser(result);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Signed out, or the probe failed: nothing useful to show. Local development
  // with AUTH_MODE=disabled still resolves a synthetic user, so the menu works
  // there too.
  if (!user) return null;

  const name = displayNameFor(user);

  return (
    <div className="user-menu" ref={containerRef}>
      <button
        type="button"
        className="user-menu-trigger"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${name}`}
        title={name}
      >
        <span className="user-menu-avatar">{initialsFor(user)}</span>
      </button>

      {open && (
        <div className="user-menu-dropdown" role="menu">
          <div className="user-menu-identity">
            <span className="user-menu-name">{name}</span>
            {user.email && <span className="user-menu-email">{user.email}</span>}
          </div>

          <button
            type="button"
            role="menuitem"
            className="user-menu-item"
            onClick={() => {
              setOpen(false);
              navigate("/knowledge");
            }}
          >
            <i className="fas fa-diagram-project" aria-hidden="true" /> Knowledge
          </button>

          <button
            type="button"
            role="menuitem"
            className="user-menu-item"
            onClick={() => {
              setOpen(false);
              navigate("/settings");
            }}
          >
            <i className="fas fa-cog" aria-hidden="true" /> Settings
          </button>

          <button
            type="button"
            role="menuitem"
            className="user-menu-item user-menu-item--danger"
            onClick={() => void logout()}
          >
            <i className="fas fa-sign-out-alt" aria-hidden="true" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
